import React, { useState } from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react';
import { staticURL } from 'core/url';
import { Link } from 'core/routing';
import {
  IconButton,
  Paragraph,
  Heading,
  Tooltip,
  Button,
  Icon,
  Box,
  Logo,
} from 'components';
import { useMemoOne } from 'use-memo-one';
import { clientStore } from 'apps/client/app';
import RelativeTime from 'components/RelativeTime';
import { markdown } from 'libs/editor/serializers/markdown';
import { Editor } from 'libs/editor';
import { useViewerControl } from 'libs/rendering/gltf/hooks';
import { useContextManager } from 'apps/context/hooks';

import { EditorStore } from 'libs/editor/store';
import {
  Edit,
  Retry,
  Delete,
  Download,
  AnnotationIcon,
  RulerIcon,
} from 'icons';
import downloadFromUrl from 'utils/downloadFromUrl';

import { Seen } from './Seen';
import { SentBy } from './SentBy';
import { ButtonGroup } from './ButtonGroup';

import {
  ChatMessageIcon,
  ReplyMessageIcon,
  ChatMessageStyled,
  SenderName,
  AvatarStyled,
  ButtonCollapse,
  ButtonDeleteMessage,
  BlockWrapper,
} from './chat.styled';

const markDownImgReg = /!\[image]\((.*?)\)/;

const getDownloadUrl = (message = '') => {
  const matches = message.match(markDownImgReg);
  if (!matches) return null;
  const [, url = ''] = matches;

  const paths = url.split('/');

  return {
    url,
    name: paths[paths.length - 1],
  };
};
const flexAlignCenter = {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'nowrap',
};

const ChatMessage = observer(props => {
  const { message, createdBy, canDelete, onDelete, isLastRead } = props;
  if (message == null) return null;

  const [isMouseOver, setMouseOver] = useState(false);

  const { body, messageType, referenceId, timestamp, annotationId } = message;

  const imageInfo = getDownloadUrl(body);

  const viewerControl = useViewerControl();
  const contextManager = useContextManager();

  const { viewer } = viewerControl;

  const { settings, notePad } = viewer || {};

  // TODO: do this when clicking the message
  const note = annotationId && notePad?.GetNote(annotationId);
  const measurement = annotationId && notePad?.GetMeasurement(annotationId);
  const currCamera = settings?.CurrentCamera();
  const belongsToTab = viewerControl.pinBelongsToCamera(note, currCamera);

  const isNote = messageType === 'activity' && annotationId && note;
  // TODO: add annotation kind into message?
  const isMeasurement =
    messageType === 'activity' && annotationId && measurement && !isNote;
  const isAnnotation = isNote || isMeasurement;

  let forCadItem = null;
  if (isNote) {
    const noteCameraData = note.GetCameraDataJsonObj();
    forCadItem = viewerControl.findCadItemForNote(note);
  }
  if (isMeasurement) {
    forCadItem = viewerControl.findCadItemForMeasurement(measurement);
  }

  // - isLoading only applies to activity messages, these need to wait until
  // the notes and measurements are loaded into the viewer, so then they can
  // determine if it is a note or a measurement, until that is possible
  // isLoading is true and we will not show an icon or the body of the message
  // for activity messages then
  const isLoading = messageType === 'activity' && !isNote && !isMeasurement;

  const isSelected = viewerControl?.isPinExpanded(referenceId);
  const isHovered = viewerControl?.isPinHovered(referenceId);

  const pending = !message.id;

  // This is only for testing, delete it later
  // TODO: is it still only for testing?
  const typesList = ['regular', 'annotation', 'payment', 'revision'];
  const typeIndex = null; // null by default, 2 set all as payment type, 3 set all as revision type

  const isRFQ = messageType === 'rfq';

  const shouldShowEdit =
    isMouseOver &&
    messageType !== 'activity' &&
    clientStore.signedInProfile.user.id === createdBy?.user?.id;

  const shouldShowDownload = isMouseOver && imageInfo;

  const account = createdBy || {};
  const { isStaff } = account;
  const shouldShowLogo = isStaff && messageType !== 'activity';

  if (!account?.avatar) {
    account.avatar = {};
  }

  if (!account.avatar?.src) {
    account.avatar.src =
      account.avatar.completed || account.avatar.completed === undefined
        ? account.avatar.url
        : account.avatar.localFile
        ? URL.createObjectURL(account.avatar.localFile)
        : '';
  }

  let type = typeIndex
    ? typesList[typeIndex]
    : isAnnotation && referenceId
    ? message.messageType
    : null;

  // Override always the type if it is an activity one
  if (type == null && isAnnotation) {
    type = 'activity';
  }

  const showAnnotationPins = () => {
    if (notePad) {
      notePad.visible = true;
    }
  };

  const PayBlock = () =>
    type === 'payment' && (
      <BlockWrapper>
        <Heading level={5} margin='large 0 0 0'>
          <>Final Quote*</>
        </Heading>
        <Heading level={3} size='large' margin='default 0'>
          <>{'00.00 €'}</>
        </Heading>
        <Button text={<>Pay</>} outline />
        <Button
          removeBold
          size='xsmall'
          appearance='plain'
          variant='minimal'
          margin='small 0 default 0'
          text={<>Cancel or Refund</>}
        />
      </BlockWrapper>
    );

  const RevisonBlock = () =>
    type === 'revision' && (
      <BlockWrapper>
        <Heading
          level={4}
          size='xsmall'
          weight='regular'
          margin='large 0 default 0'
        >
          <>Your design needs some changes. Please upload an updated version.</>
        </Heading>
        <Button
          outline
          variant='tertiary'
          size='small'
          margin='small 0 default 0'
          text={<>Upload New Version</>}
        />
      </BlockWrapper>
    );

  const pinTargetType =
    referenceId != null && viewerControl?.pinTargetType(referenceId);
  const editorStore = useMemoOne(() => {
    return new EditorStore({
      formatting: 'markdown',
      initialValue: markdown.deserialize(body),
      readOnly: true,
    });
  }, [body]);

  const handleSaveBtnClick = () => {
    editorStore.setMarkdownValue();
    if (editorStore.markdownValue !== '') {
      contextManager.UpdateMessage(message.id, editorStore.markdownValue);
      editorStore.SetEditable(false);
    }
  };

  const handleCancelBtnClick = () => {
    editorStore.onChange({ value: markdown.deserialize(body) });
    editorStore.SetEditable(false);
  };

  const handleEditBtnClick = () => {
    editorStore.SetEditable(true);
  };

  const handleOnClick = () => {
    if (isNote && forCadItem) {
      showAnnotationPins();
      viewerControl?.TogglePinExpand(note.key);
    } else if (isMeasurement && forCadItem) {
      if (measurement.forCadId !== currCamera.GetItem().uuid) {
        viewerControl.InspectById(measurement.forCadId);
      } else {
        viewerControl.CameraToMeasurement(annotationId);
      }
    }
  };

  const handleMouseEnter = () => {
    setMouseOver(true);
    if (isNote && forCadItem) {
      viewerControl?.HighlightPinTarget(annotationId)
    } else if (isMeasurement && forCadItem) {
      viewerControl?.SelectAndHighlightMeasurement(annotationId)
    }
  }
  const handleMouseLeave = () => {
    setMouseOver(false);
    if (forCadItem) {
      viewerControl?.UnHighlightEverything();
    }
  }

  let avatarSrc = account.avatar?.url;
  let avatarName = account.fullName;
  if (isRFQ) {
    avatarSrc = staticURL('assets/logos/wikifactory-black.png');
    avatarName = 'Wikifactory';
  }

  const WikiFactoryBadge = shouldShowLogo && (
    <>
      <Link name='home'>
        <Logo variant='black' className='w-6 mr-1' />
      </Link>
      &nbsp;
    </>
  );
  const MessageIcon =
    !shouldShowLogo &&
    !isAnnotation &&
    !isLoading && (
      <Icon
        name='chat_multiple'
        size='medium'
        className='mr-2 cursor-pointer'
      />,
    );

  let senderNameElement = (
    <SenderName style={flexAlignCenter} level={4} size='xsmall'>
      {isRFQ === true ? (
        <span>Wikifactory&nbsp;</span>
      ) : (
        <Box style={flexAlignCenter} className='mr-1'>
          {WikiFactoryBadge}
          {MessageIcon}
          <Link name='profile' params={{ profile: createdBy?.username }}>
            {createdBy?.username}
          </Link>
          {isNote && (
            <div className='ml-1 font-normal'>created an Annotation</div>
          )}
          {isMeasurement && (
            <div className='ml-1 font-normal'>created a Measurement</div>
          )}
        </Box>
      )}
      <RelativeTime date={timestamp} />
    </SenderName>
  );

  let sentByElement = (
    <>
      {message.sentAs ? (
        <SentBy
          createdBy={createdBy.username}
          sentAsName={
            message.sentAs.username || message.sentAs.profile.username
          }
        />
      ) : null}
    </>
  );
  let seenElement = <>{message.readByCustomer ? <Seen /> : null}</>;
  let buttonsGroupElement = null;
  let editorStyle = {};
  if (isAnnotation && !forCadItem) {
    editorStyle = { textDecoration: 'line-through' };
  }

  let editorWrapperStyle = {
    display: 'flex',
    alignItems: 'baseline',
  };

  const isEditable = !editorStore.readOnly;
  const currentMessageSendFailed = contextManager.failedMessages.find(
    failedMessage => failedMessage.tempKey === message.tempKey,
  );

  if (isEditable) {
    senderNameElement = null;
    sentByElement = null;
    seenElement = null;
    editorStyle = { background: 'white' };
    editorWrapperStyle = {
      display: 'block',
    };
  }

  if (shouldShowEdit || shouldShowDownload) {
    buttonsGroupElement = (
      <ButtonGroup>
        {shouldShowEdit && (
          <IconButton
            onClick={handleEditBtnClick}
            size='small'
            className='mr-2'
            icon={<Edit />}
            variant='outlined'
          />
        )}
        {shouldShowDownload && (
          <IconButton
            size='small'
            variant='outlined'
            icon={<Download />}
            onClick={() => {
              downloadFromUrl(imageInfo.url, imageInfo.name);
            }}
          />
        )}
      </ButtonGroup>
    );
  }

  const renderAction = () => {
    const shouldRenderFailedAction = currentMessageSendFailed && isMouseOver;

    // if failed to send, then show up the delete & retry icon when user hover item
    if (shouldRenderFailedAction) {
      return (
        <ButtonGroup>
          <IconButton
            className='mr-2'
            icon={<Retry />}
            data-testid='retry-send-message'
            variant='outlined'
            onClick={() => {
              contextManager.resendMessage(message);
            }}
            size='small'
          />
          <IconButton
            icon={<Delete />}
            size='small'
            variant='outlined'
            onClick={() => {
              contextManager.deleteSendFailedMessage(message.tempKey);
            }}
          />
        </ButtonGroup>
      );
    }

    return !isEditable ? (
      buttonsGroupElement
    ) : (
      <Box
        sx={{
          position: 'absolute',
          display: 'flex',
          right: '1rem',
          bottom: '0.25rem',
        }}
      >
        <Button
          onClick={handleCancelBtnClick}
          text='Cancel'
          size='xsmall'
          variant='tertiary'
          outline
          style={{
            minWidth: 'auto',
            marginRight: '0.5rem',
            boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.1)',
          }}
        />
        <Button
          onClick={handleSaveBtnClick}
          text='Save'
          outline
          size='xsmall'
          style={{
            minWidth: 'auto',
            boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.1)',
          }}
        />
      </Box>
    );
  };

  const renderMessageHeader = () => {
    if (currentMessageSendFailed) {
      if (currentMessageSendFailed.resending) {
        return (
          <div className='flex'>
            <span className='text-12 text-blue-500'>Resending...</span>
          </div>
        );
      }

      return (
        <div className='flex'>
          <Icon name='warning' colorPath='red' />
          <span
            data-testid='failed-to-send'
            className='text-red-500 text-12 ml-1'
          >
            Failed to send
          </span>
        </div>
      );
    }

    return (
      <div
        className=' inline-flex'
      >
        {isNote && <AnnotationIcon className='mr-2 cursor-pointer' />}
        {isMeasurement && <RulerIcon className='mr-2 cursor-pointer' />}
        {senderNameElement}
        {seenElement}
      </div>
    );
  };

  const renderMessageAvatar = () => {
    if (type === 'automated') {
      return (
        <ChatMessageIcon
          type={type}
          active={belongsToTab}
          targetType={pinTargetType}
          highlighted={isHovered}
          expanded={isSelected}
        >
          <img
            src={staticURL('assets/chat/botler.svg')}
            alt='Botler'
            style={{ borderRadius: '50%' }}
          />
        </ChatMessageIcon>
      );
    }

    return referenceId ? (
      <ReplyMessageIcon
        onClick={() => viewerControl?.TogglePinExpand(referenceId)}
      >
        <AvatarStyled src={avatarSrc} name={avatarName} />
      </ReplyMessageIcon>
    ) : (
      <Link name='profile' params={{ profile: createdBy?.username }}>
        <AvatarStyled src={avatarSrc} name={avatarName} />
      </Link>
    );
  };

  return (
    <>
      <ChatMessageStyled
        editable={isEditable}
        pending={pending}
        index={props.index}
        active={belongsToTab}
        expanded={isSelected}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        type={type}
        onClick={handleOnClick}
        className={isAnnotation ? ' cursor-pointer' : ''}
      >
        {renderAction()}
        <Tooltip delayShow={500} place='left' />
        {renderMessageAvatar()}
        <div>
          <Box
            sx={{
              justifyContent: 'space-between',
              alignItems: 'center',
              display: 'flex',
            }}
            mb={isAnnotation ? 0 : 2}
          >
            {renderMessageHeader()}
          </Box>
          {!isLoading &&
            body &&
            type !== 'payment' &&
            type !== 'revision' && (
              <Box sx={editorWrapperStyle}>
                <Editor
                  store={editorStore}
                  placeholder='Your message here'
                  previewMode='chat'
                  style={editorStyle}
                />
                {isAnnotation && !forCadItem && <Paragraph size='xsmall' variant='light' ml={2}>
                   (outdated)
                 </Paragraph>}
                {!isEditable && message.updatedAt ? (
                  <Paragraph size='xsmall' variant='light' ml={2}>
                    (edited)
                  </Paragraph>
                ) : null}
              </Box>
            )}
          {!isAnnotation && (
            <>
              {sentByElement}
              <PayBlock />
              <RevisonBlock />
            </>
          )}
        </div>
        {canDelete && messageType === 'regular' && !pending && (
          <ButtonDeleteMessage
            icon='delete'
            size='xsmall'
            data-tip='Delete'
            ariaLabel='Delete Messages'
            appearance='plain'
            variant='destructive'
            onClick={() => onDelete()}
          />
        )}
      </ChatMessageStyled>
      {isLastRead && (
        <>
          <NewMessageGroup className='test'>
            <ScrollDownWrapper>
              <StyledP>New</StyledP>
              <Button
                outline
                icon='arrow_down'
                size='xsmall'
                variant='minimal'
                onClick={() => {
                  props.onScrollToBottom({ setRead: true });
                }}
              />
            </ScrollDownWrapper>
          </NewMessageGroup>
        </>
      )}
    </>
  );
});

const NewMessageGroup = styled.div`
  display: flex;
  align-items: center;
  position: relative;
`;

const ScrollDownWrapper = styled.div`
  right: 24px;
  position: absolute;
  background: #fff;
  align-items: center;
  padding: 0 8px;
  display: flex;
  z-index: 200;
`;

const StyledP = styled.p`
  font-size: 14px;
  margin-right: 8px;
  color: #8b8b8b;
`;

export default ChatMessage;
